import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';

/**
 * Floating "to the objects" anchor button for the Soodam Edit detail page.
 *
 * Visible from initial paint, hides only when the objects section enters the
 * upper 75% of the viewport (so it gets out of the way once the visitor has
 * reached the products). The persimmon stone child element gets its own
 * delayed slide-in to echo the brand's placed-stone bug motion.
 *
 * @extends {Component<{ button: HTMLButtonElement }>}
 */
class SoodamSkipToObjects extends Component {
  requiredRefs = ['button'];

  #shouldHide = false;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {Element | null} */
  #scrollTarget = null;
  /** @type {Element | null} */
  #hideTarget = null;
  /** @type {number | undefined} */
  #entranceTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#scrollTarget = document.getElementById('soodam-edit-page-objects-label');
    this.#hideTarget = document.querySelector('.soodam-edit-page__object');
    if (!this.#scrollTarget || !this.#hideTarget) {
      this.remove();
      return;
    }

    // Hide the moment any pixel of the first product enters the viewport.
    this.#observer = new IntersectionObserver((entries) => this.#handleIntersect(entries));
    this.#observer.observe(this.#hideTarget);

    this.refs.button.addEventListener('click', this.#handleClick);

    // Hold the entrance for a beat after page load so the button arrives
    // as its own moment rather than rendering with the rest of the page.
    this.#entranceTimer = window.setTimeout(() => {
      this.#setVisible(!this.#shouldHide);
    }, 2000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.refs.button?.removeEventListener('click', this.#handleClick);
    if (this.#entranceTimer) window.clearTimeout(this.#entranceTimer);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target === this.#hideTarget) {
        // Hide once any pixel of the first product is on screen, and keep
        // it hidden after the user has scrolled past the products entirely
        // (bounding rect's top edge is above the viewport).
        const scrolledPast = entry.boundingClientRect.top < 0;
        this.#shouldHide = entry.isIntersecting || scrolledPast;
      }
    }
    this.#setVisible(!this.#shouldHide);
  };

  /** @param {boolean} visible */
  #setVisible = (visible) => {
    this.dataset.visible = visible ? 'true' : 'false';
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    event.preventDefault();
    if (this.#scrollTarget) {
      scrollIntoView(this.#scrollTarget, { behavior: 'smooth', block: 'start' });
    }
  };
}

if (!customElements.get('soodam-skip-to-objects')) {
  customElements.define('soodam-skip-to-objects', SoodamSkipToObjects);
}
